import { z } from "zod";
import {
  MAX_PIX_CENTS,
  PIX_DEFAULT_EXPIRATION_S,
  PIX_MAX_EXPIRATION_S,
  PIX_MERCHANT_CITY_MAX,
  PIX_MERCHANT_NAME_MAX,
  PIX_MIN_EXPIRATION_S,
  pixText,
  type PixKeyKind,
  type PixReviewReason,
} from "@cx/domain";
import { ulidParam } from "./ids";

// ===== Cobrança Pix (T1.10) =====

/** Asaas é o provedor real padrão (decisão da Fabi, 03/10/2026); Efí continua disponível. */
export const PIX_PROVIDERS = ["sandbox", "asaas", "efi"] as const;
export type PixProviderId = (typeof PIX_PROVIDERS)[number];

const expiration = z
  .number("Informe a validade")
  .int("Validade inválida")
  .min(PIX_MIN_EXPIRATION_S, "Validade mínima de 5 minutos")
  .max(PIX_MAX_EXPIRATION_S, "Validade máxima de 30 dias");

const merchant = (what: string, max: number) =>
  z
    .string(`Informe ${what}`)
    .max(200, `Informe ${what}`)
    .transform((s) => pixText(s, max))
    .refine((s) => s.length > 0, `Informe ${what} (letras e números)`);

/** Credenciais do Efí: Client ID, Client Secret e certificado .p12 (base64). Só entram; nunca saem. */
export const efiCredentialsInput = z.object({
  clientId: z.string().trim().min(1, "Informe o Client ID").max(200, "Até 200 caracteres"),
  clientSecret: z.string().trim().min(1, "Informe o Client Secret").max(200, "Até 200 caracteres"),
  certificateBase64: z
    .string()
    .trim()
    .min(1, "Envie o certificado .p12")
    .max(24_000, "Certificado grande demais")
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, "Certificado inválido (base64)"),
  /** Homologação (sandbox do Efí) ou produção. */
  environment: z.enum(["homologation", "production"], "Ambiente inválido").default("homologation"),
});
export type EfiCredentialsInput = z.infer<typeof efiCredentialsInput>;

/** Prefixo das chaves de API do Asaas geradas desde 10/03/2025 (chaves antigas não têm e continuam valendo). */
export const ASAAS_KEY_PREFIX = { sandbox: "$aact_hmlg_", production: "$aact_prod_" } as const;

/** Credenciais do Asaas: chave de API (cabeçalho `access_token`) e ambiente. Só entram; nunca saem. */
export const asaasCredentialsInput = z
  .object({
    apiKey: z
      .string("Informe a chave de API")
      .trim()
      .min(1, "Informe a chave de API")
      .max(500, "Chave de API inválida")
      .regex(/^[\x21-\x7e]*$/, "Chave de API inválida"),
    /** Sandbox (api-sandbox.asaas.com) ou produção (api.asaas.com). */
    environment: z.enum(["sandbox", "production"], "Ambiente inválido").default("sandbox"),
  })
  .superRefine((c, ctx) => {
    const other = c.environment === "sandbox" ? "production" : "sandbox";
    if (c.apiKey.startsWith(ASAAS_KEY_PREFIX[other]))
      ctx.addIssue({
        code: "custom",
        path: ["apiKey"],
        message:
          other === "production"
            ? "Esta chave é do ambiente de produção do Asaas"
            : "Esta chave é do ambiente sandbox do Asaas",
      });
  });
export type AsaasCredentialsInput = z.infer<typeof asaasCredentialsInput>;

/**
 * `credentials` ausente ou null = mantém as gravadas. `rotateWebhookSecret` gera um segredo/token novo do webhook (o
 * antigo deixa de valer) e cadastra a URL de novo no provedor; credenciais novas e troca de provedor também renovam.
 */
const pixSettingsBase = z.object({
  provider: z.enum(PIX_PROVIDERS, "Provedor inválido"),
  active: z.boolean().default(true),
  pixKey: z.string("Informe a chave Pix").trim().min(1, "Informe a chave Pix").max(100, "Chave Pix inválida"),
  merchantName: merchant("o nome do recebedor", PIX_MERCHANT_NAME_MAX),
  merchantCity: merchant("a cidade do recebedor", PIX_MERCHANT_CITY_MAX),
  financialAccountId: ulidParam,
  paymentMethodId: ulidParam.nullish().transform((v) => v ?? null),
  expirationSeconds: expiration.default(PIX_DEFAULT_EXPIRATION_S),
  credentials: z
    .record(z.string(), z.unknown(), "Credenciais inválidas")
    .nullish()
    .transform((v) => v ?? null),
  rotateWebhookSecret: z.boolean("Valor inválido").default(false),
  /**
   * E-mail de contato da empresa para os avisos do provedor sobre o webhook (Asaas: fila interrompida). Revisão de
   * segurança do Asaas (B5). Ausente/vazio = mantém o gravado ou usa o e-mail da empresa; o Asaas exige um.
   */
  notifyEmail: z
    .string("E-mail inválido")
    .trim()
    .max(254, "E-mail inválido")
    .nullish()
    .transform((v) => (v ? v.toLowerCase() : null))
    .pipe(z.email("E-mail inválido").nullable()),
});

/** Credenciais conferidas pelo formato do provedor escolhido (o sandbox não usa credenciais: ficam null). */
export const pixSettingsInput = pixSettingsBase.transform((i, ctx) => {
  if (!i.credentials || i.provider === "sandbox") return { ...i, credentials: null };
  const r =
    i.provider === "asaas"
      ? asaasCredentialsInput.safeParse(i.credentials)
      : efiCredentialsInput.safeParse(i.credentials);
  if (!r.success) {
    for (const issue of r.error.issues)
      ctx.addIssue({ code: "custom", message: issue.message, path: ["credentials", ...issue.path] });
    return z.NEVER;
  }
  return { ...i, credentials: r.data as EfiCredentialsInput | AsaasCredentialsInput };
});
export type PixSettingsInput = z.infer<typeof pixSettingsInput>;

export type PixSettingsView = {
  provider: PixProviderId;
  active: boolean;
  pixKey: string;
  pixKeyKind: PixKeyKind;
  merchantName: string;
  merchantCity: string;
  financialAccountId: string;
  financialAccountName: string | null;
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  expirationSeconds: number;
  hasCredentials: boolean;
  /** E-mail de contato dos avisos do provedor sobre o webhook (B5). */
  notifyEmail: string | null;
  /** Caminho público do webhook sem o token secreto (o caminho completo só vai para o provedor). */
  webhookPath: string;
  updatedAt: string;
};

export const pixChargeCreateInput = z.object({
  titleId: ulidParam,
  /** Parte do saldo cobrada; ausente = saldo em aberto. */
  amountCents: z
    .number("Informe o valor")
    .int("Use centavos inteiros")
    .min(1, "Informe o valor")
    .max(MAX_PIX_CENTS, "Valor acima do limite do Pix")
    .nullish()
    .transform((v) => v ?? null),
  /** Ausente = a validade padrão da configuração. */
  expirationSeconds: expiration.nullish().transform((v) => v ?? null),
});
/**
 * Saúde do webhook do provedor (Asaas, B1), guardada no KV pela consulta manual e pela varredura agendada:
 * fila interrompida/desativada ou não cadastrada, e a última notificação fora do formato (ignorada com 200).
 */
export type PixWebhookHealth = {
  checkedAt: string | null;
  registered: boolean | null;
  enabled: boolean | null;
  interrupted: boolean | null;
  lastInvalidAt: string | null;
};

export type PixChargeCreateInput = z.infer<typeof pixChargeCreateInput>;

export const pixChargeListQuery = z.object({ titleId: ulidParam });

export const pixSimulateInput = z.object({
  /** Ausente = o valor da cobrança. */
  amountCents: z
    .number("Informe o valor")
    .int("Use centavos inteiros")
    .min(1, "Informe o valor")
    .max(MAX_PIX_CENTS, "Valor acima do limite do Pix")
    .nullish()
    .transform((v) => v ?? null),
});
export type PixSimulateInput = z.infer<typeof pixSimulateInput>;

export type PixChargeStatus = "creating" | "active" | "expired" | "paid" | "canceled" | "failed";
export type PixPaymentStatus = "received" | "applied" | "review" | "rejected";

export type PixPaymentView = {
  id: string;
  endToEndId: string;
  amountCents: number;
  paidAt: string;
  source: "webhook" | "poll" | "simulated";
  status: PixPaymentStatus;
  reviewReason: PixReviewReason | string | null;
  settlementId: string | null;
  excessCents: number;
};

export type PixChargeView = {
  id: string;
  titleId: string;
  provider: PixProviderId;
  txid: string;
  status: PixChargeStatus;
  amountCents: number;
  principalCents: number;
  interestCents: number;
  fineCents: number;
  paidCents: number;
  /** BR Code "copia e cola"; null enquanto o provedor não confirmou. */
  emv: string | null;
  expiresAt: string;
  errorMessage: string | null;
  createdAt: string;
  paidAt: string | null;
  canceledAt: string | null;
  payments: PixPaymentView[];
};
